import React from "react";
import { Link } from "react-router-dom";
import { Cpu, Twitter, Linkedin, Github, MessageSquare } from "lucide-react";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="bg-slate-950 text-slate-400 pt-16 pb-12 border-t border-slate-900">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand Col */}
          <div className="flex flex-col gap-6">
            <Link to="/" data-testid="footer-logo" className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center">
                <Cpu className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg tracking-tight text-white font-heading whitespace-nowrap leading-none">
                <span className="font-bold">BizLogic</span>
                <span className="font-normal"> Developers</span>
              </span>
            </Link>
            <p className="text-sm leading-relaxed text-slate-400">
              Transforming ideas into digital success. We craft premium websites, Shopify store fronts, custom mobile applications, and high-performance SaaS solutions.
            </p>
            <div className="flex items-center gap-4 mt-2">
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noreferrer"
                data-testid="footer-social-twitter"
                className="w-8 h-8 rounded-lg bg-slate-900 hover:bg-blue-600 hover:text-white flex items-center justify-center transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="w-4 h-4" />
              </a>
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noreferrer"
                data-testid="footer-social-linkedin"
                className="w-8 h-8 rounded-lg bg-slate-900 hover:bg-blue-600 hover:text-white flex items-center justify-center transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a
                href="https://github.com"
                target="_blank"
                rel="noreferrer"
                data-testid="footer-social-github"
                className="w-8 h-8 rounded-lg bg-slate-900 hover:bg-blue-600 hover:text-white flex items-center justify-center transition-colors"
                aria-label="GitHub"
              >
                <Github className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Services Col */}
          <div>
            <h3 className="text-white font-semibold text-sm tracking-wider uppercase mb-6 font-heading">
              Our Services
            </h3>
            <ul className="flex flex-col gap-3.5 text-sm">
              <li>
                <Link to="/services/web-development" data-testid="footer-link-srv-web" className="hover:text-cyan-400 transition-colors">
                  Website Development
                </Link>
              </li>
              <li>
                <Link to="/services/shopify-development" data-testid="footer-link-srv-shopify" className="hover:text-cyan-400 transition-colors">
                  Shopify Store Development
                </Link>
              </li>
              <li>
                <Link to="/services/mobile-development" data-testid="footer-link-srv-mobile" className="hover:text-cyan-400 transition-colors">
                  Mobile App Development
                </Link>
              </li>
              <li>
                <Link to="/services/ui-ux-design" data-testid="footer-link-srv-ui" className="hover:text-cyan-400 transition-colors">
                  UI/UX Design
                </Link>
              </li>
              <li>
                <Link to="/services/custom-applications" data-testid="footer-link-srv-custom" className="hover:text-cyan-400 transition-colors">
                  Custom Web Applications
                </Link>
              </li>
              <li>
                <Link to="/services/websiteseo" data-testid="footer-link-srv-seo" className="hover:text-cyan-400 transition-colors">
                  Website SEO
                </Link>
              </li>
            </ul>
          </div>

          {/* Quick Links Col */}
          <div>
            <h3 className="text-white font-semibold text-sm tracking-wider uppercase mb-6 font-heading">
              Quick Links
            </h3>
            <ul className="flex flex-col gap-3.5 text-sm">
              <li>
                <Link to="/" data-testid="footer-link-home" className="hover:text-cyan-400 transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about" data-testid="footer-link-about" className="hover:text-cyan-400 transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/services" data-testid="footer-link-services" className="hover:text-cyan-400 transition-colors">
                  Our Services
                </Link>
              </li>
              <li>
                <Link to="/website-care" data-testid="footer-link-website-care" className="hover:text-cyan-400 transition-colors">
                  Website Care
                </Link>
              </li>
              <li>
                <Link to="/contact" data-testid="footer-link-contact" className="hover:text-cyan-400 transition-colors">
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Col */}
          <div className="min-w-0">
            <h3 className="text-white font-semibold text-sm tracking-wider uppercase mb-6 font-heading">
              Contact Us
            </h3>
            <ul className="flex flex-col gap-4 text-sm">
              <li className="flex items-start gap-3">
                <span className="text-blue-500 font-semibold shrink-0">Address:</span>
                <span className="min-w-0 text-pretty xl:whitespace-nowrap">Old Bridge, NJ 08857</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="text-blue-500 font-semibold shrink-0">Email:</span>
                <a href="mailto:bizlogicdeveloper@gmail.com" data-testid="footer-email-link" className="hover:text-cyan-400 transition-colors">
                  bizlogicdeveloper@gmail.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <span className="text-blue-500 font-semibold shrink-0">Phone:</span>
                <a href="tel:9083798171" data-testid="footer-phone-link" className="hover:text-cyan-400 transition-colors">
                  908-379-8171
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between pt-8 border-t border-slate-900 text-xs">
          <p data-testid="footer-copyright">
            &copy; {currentYear} BizLogic. All rights reserved. Built with precision and care.
          </p>
          <div className="flex items-center gap-6 mt-4 sm:mt-0">
            <span className="hover:text-white transition-colors cursor-pointer">Privacy Policy</span>
            <span className="hover:text-white transition-colors cursor-pointer">Terms of Service</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
